import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Card, Skeleton } from "@libs/rubick";
import { formatBytesPrecise } from "@/utils/formatters";
function ComparisonBar({ label, fraction, variant }) {
    const pct = Math.min(100, Math.max(0, fraction * 100));
    const barClass = variant === "today" ? "bg-gradient-to-r from-cyan-400 to-green-400" : "bg-gray-400";
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-3",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "w-20 text-xs font-medium text-gray-500 text-right",
                children: label
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative flex-1 h-3 bg-gray-200 rounded-full overflow-hidden",
                children: /*#__PURE__*/ _jsx("div", {
                    className: `absolute inset-y-0 left-0 rounded-full ${barClass}`,
                    style: {
                        width: `${pct}%`
                    }
                })
            })
        ]
    });
}
function padHour(hour) {
    return `${String(hour).padStart(2, "0")}:00`;
}
export function UsageComparison24h({ data, isLoading }) {
    const { todayBytes, yesterdayBytes, deltaPct, deltaBytes, trending, comparedAtHourUtc, avgPerHourToday, avgPerHourYesterday, peakDelta } = data;
    const maxBytes = Math.max(todayBytes, yesterdayBytes);
    const todayFraction = maxBytes > 0 ? todayBytes / maxBytes : 0;
    const yesterdayFraction = maxBytes > 0 ? yesterdayBytes / maxBytes : 0;
    const trendingUp = trending === "up";
    const trendingFlat = trending === "flat";
    const arrow = trendingFlat ? "" : trendingUp ? "\u2191" : "\u2193";
    const deltaSign = deltaBytes >= 0 ? "+" : "";
    return /*#__PURE__*/ _jsx(Card, {
        variant: "bordered",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-5",
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    className: "text-sm font-semibold uppercase tracking-wider text-gray-500",
                    children: "24h Usage Comparison"
                }),
                isLoading ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-end justify-between",
                            children: [
                                /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-10 w-28"
                                }),
                                /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-8 w-20"
                                }),
                                /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-10 w-28"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-3 w-full"
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-3 w-full"
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-end justify-between",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs uppercase tracking-wider text-gray-500",
                                            children: "Today"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-3xl font-bold text-green-500",
                                            children: formatBytesPrecise(todayBytes)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-center",
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: `text-2xl font-bold ${trendingFlat ? "text-gray-500" : trendingUp ? "text-green-500" : "text-red-500"}`,
                                            children: [
                                                arrow,
                                                " ",
                                                Math.abs(deltaPct).toFixed(1),
                                                "%"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-xs text-gray-500",
                                            children: [
                                                deltaSign,
                                                formatBytesPrecise(Math.abs(deltaBytes))
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-right",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs uppercase tracking-wider text-gray-500",
                                            children: "Yesterday"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-3xl font-bold text-gray-900",
                                            children: formatBytesPrecise(yesterdayBytes)
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(ComparisonBar, {
                                    label: "Today",
                                    fraction: todayFraction,
                                    variant: "today"
                                }),
                                /*#__PURE__*/ _jsx(ComparisonBar, {
                                    label: "Yesterday",
                                    fraction: yesterdayFraction,
                                    variant: "yesterday"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between",
                            children: [
                                /*#__PURE__*/ _jsx(Badge, {
                                    color: trendingFlat ? "zinc" : trendingUp ? "green" : "red",
                                    children: trendingFlat ? "No change" : `Trending ${trending}`
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-xs text-gray-500",
                                    children: [
                                        "Compared at same hour (",
                                        padHour(comparedAtHourUtc),
                                        " UTC)"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-8 border-t border-gray-200 pt-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs uppercase tracking-wider text-gray-400",
                                            children: "Avg / Hour (Today)"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm font-medium text-gray-900",
                                            children: formatBytesPrecise(avgPerHourToday)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs uppercase tracking-wider text-gray-400",
                                            children: "Avg / Hour (Yesterday)"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm font-medium text-gray-900",
                                            children: formatBytesPrecise(avgPerHourYesterday)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs uppercase tracking-wider text-gray-400",
                                            children: "Peak Delta"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm font-medium text-gray-900",
                                            children: peakDelta ? `${peakDelta.bytes >= 0 ? "+" : "-"}${formatBytesPrecise(Math.abs(peakDelta.bytes))} at ${padHour(peakDelta.hour)}` : "\u2014"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
